import React, { JSX } from 'react';
import { formatApiDateTime, formatTime, formatUserIdentityDisplay } from '../../../helpers/helpers';
import TableLoadingOverlay from '../../shared/TableLoadingOverlay';
import type { ExplorerDataset } from '../types/DataExplorerTypes';
import { deviceMappingColumnLabel } from '../../../helpers/deviceMappingRows';
import TableViewer from './TableViewer';

type DataExplorerTableProps = {
  dataset: ExplorerDataset;
  explorerBusy: boolean;
  deviceIdSearch: string;
  filteredExplorerRows: Record<string, unknown>[];
  explorerRows: Record<string, unknown>[];
  explorerColumns: string[];
  userEmailBySub: Record<string, string>;
  deviceIdColumn: string;
  onRemoveRow: (rowIndex: number) => void | Promise<void>;
};

export default function DataExplorerTable(props: DataExplorerTableProps): JSX.Element {
  const {
    dataset,
    explorerBusy,
    deviceIdSearch,
    filteredExplorerRows,
    explorerRows,
    explorerColumns,
    userEmailBySub,
    onRemoveRow,
  } = props;

  const showActionsColumn = dataset === 'deviceNotes';
  const visibleExplorerColumns = explorerColumns.filter((col) => {
    if (dataset !== 'deviceLifecycle') return true;
    return String(col).trim().toLowerCase() !== 'recordedat';
  });

  return (
    <div className="flex-1 min-h-0 p-3 bg-gray-50 dark:bg-slate-800">
      <div className="h-full min-h-0 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
        <TableLoadingOverlay loading={explorerBusy} label="Loading data..." className="h-full min-h-0">
          <TableViewer>
            {filteredExplorerRows.length === 0 ? (
              <div className="p-8 text-sm text-gray-600 dark:text-slate-300">
                {explorerBusy
                  ? 'Loading data...'
                  : deviceIdSearch.trim()
                    ? 'No matching rows.'
                    : 'No data loaded.'}
              </div>
            ) : (
              <table className="w-max min-w-full text-xs border-separate border-spacing-0">
                <thead className="bg-gray-50 dark:bg-slate-800 sticky top-0 z-10 shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                  <tr>
                    {visibleExplorerColumns.map((col) => (
                      <th key={col} className="text-left p-2.5 border-b border-gray-200 dark:border-slate-700 whitespace-nowrap font-semibold text-gray-700 dark:text-slate-300">
                        {dataset === 'deviceMappings' ? deviceMappingColumnLabel(col) : col}
                      </th>
                    ))}
                    {showActionsColumn && (
                      <th className="text-left p-2.5 border-b border-gray-200 dark:border-slate-700 whitespace-nowrap font-semibold text-gray-700 dark:text-slate-300">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredExplorerRows.map((row, i) => {
                    const rowIndex = explorerRows.indexOf(row);
                    return (
                      <tr key={`row-${rowIndex}-${i}`} className="odd:bg-white dark:bg-slate-900 even:bg-gray-50 dark:even:bg-slate-800 hover:bg-blue-50/30 transition-colors">
                        {visibleExplorerColumns.map((col) => {
                          const raw = row[col];
                          const normalizedCol = String(col).trim().toLowerCase();
                          const normalizedColKey = normalizedCol.replace(/\s+/g, '').replace(/_/g, '');
                          let text =
                            raw == null
                              ? ''
                              : typeof raw === 'string'
                                ? raw
                                : JSON.stringify(raw);

                          if (dataset === 'deviceNotes') {
                            const c = String(col).trim();
                            if (c === 'createdAt') {
                              const n =
                                typeof raw === 'number'
                                  ? raw
                                  : Number(
                                    typeof raw === 'string'
                                      ? raw
                                      : JSON.stringify(raw),
                                  );
                              const t = formatTime(n);
                              text = t || String(raw ?? '');
                            }
                          }
                          if (dataset === 'deviceLifecycle') {
                            const lifecycleTimeColumns = new Set(['effectiveat', 'updatedat']);
                            if (lifecycleTimeColumns.has(normalizedColKey)) {
                              text = formatApiDateTime(raw, '') || String(raw ?? '');
                            }
                          }

                          const identityColumns = new Set([
                            'createdby',
                            'assignedby',
                            'updatedby',
                            'statusupdatedby',
                            'editedby',
                          ]);
                          if (identityColumns.has(normalizedColKey)) {
                            const identity = typeof raw === 'string' ? raw : String(raw ?? '');
                            text = formatUserIdentityDisplay(identity, userEmailBySub);
                          }

                          const isRequestPayloadColumn =
                            dataset === 'requests'
                            && (normalizedCol === 'bodyjson'
                              || normalizedCol === 'body'
                              || normalizedCol === 'payload');
                          return (
                            <td
                              key={`${i}-${col}`}
                              className={`p-2.5 border-b border-gray-200 dark:border-slate-700 align-top text-gray-700 dark:text-slate-300 ${isRequestPayloadColumn ? 'whitespace-normal' : 'whitespace-nowrap'
                                }`}
                            >
                              <div
                                className={
                                  isRequestPayloadColumn
                                    ? 'max-w-[900px] whitespace-pre-wrap break-all'
                                    : ''
                                }
                              >
                                {text}
                              </div>
                            </td>
                          );
                        })}

                        {showActionsColumn && (
                          <td className="p-2.5 border-b border-gray-200 dark:border-slate-700 align-top whitespace-nowrap">
                            <button
                              type="button"
                              className="px-2.5 py-1.5 text-xs rounded-md border border-red-200 dark:border-red-700/60 text-red-700 bg-red-50 hover:bg-red-100"
                              onClick={() => {
                                void onRemoveRow(rowIndex);
                              }}
                            >
                              Delete Note
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </TableViewer>
        </TableLoadingOverlay>
      </div>
    </div>
  );
}
